/**
 * @internal
 */
import React, { createContext, forwardRef, useCallback, useContext, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import useScrollEdges from '@polestar/component-warehouse-react/hooks/useScrollEdges';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import useScrollTo from '@polestar/component-warehouse-react/hooks/useScrollTo';
const ScrollViewEnhancedContext = createContext({
    onSelect: () => null,
});
export const useScrollViewEnhanced = () => {
    return useContext(ScrollViewEnhancedContext);
};
const ScrollViewEnhanced = forwardRef(({ onWheel, children, scrollOffset = 0.3, ...props }, ref) => {
    const scrollRef = useRef(null);
    const { handleScroll, scrollEdgesRef, reachedLeft, reachedRight } = useScrollEdges(1);
    const combinedRef = useCombinedRef(ref, scrollRef, scrollEdgesRef);
    const { isRTL } = useDirection();
    const { motion, reset, stop } = useScrollTo(0, scrollRef, 'scrollLeft');
    // Has scroll
    const tabsOverflowContainer = true;
    const handleWheel = useCallback((e) => {
        stop();
        onWheel === null || onWheel === void 0 ? void 0 : onWheel(e);
    }, [onWheel, stop]);
    const handleSelect = useCallback((node) => {
        if (!node || !(scrollRef === null || scrollRef === void 0 ? void 0 : scrollRef.current)) {
            return;
        }
        const rect = node.getBoundingClientRect();
        const currentScroll = scrollRef.current.scrollLeft;
        const containerRect = scrollRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const currentPosition = rect.left - containerRect.left + rect.width / 2;
        const newScroll = rect.left - containerRect.left + currentScroll + rect.width / 2;
        const startOffset = containerWidth * scrollOffset;
        const endOffset = containerWidth * (1 - scrollOffset);
        let newEnd = null;
        if (currentPosition < startOffset) {
            newEnd = newScroll - startOffset;
        }
        else if (currentPosition > endOffset) {
            newEnd = newScroll - endOffset;
        }
        if (newEnd !== null) {
            reset();
            motion.set(Math.min(Math.max(0, newEnd), scrollRef.current.scrollWidth - scrollRef.current.offsetWidth), false);
        }
    }, [motion, reset, scrollOffset]);
    return (React.createElement(ScrollViewEnhancedContext.Provider, { value: { onSelect: handleSelect } },
        React.createElement(Container, { ...props, ref: combinedRef, onScroll: handleScroll, onWheel: handleWheel, scrollOverflow: tabsOverflowContainer, startGradientVisible: ((isRTL && !reachedRight) || (!isRTL && !reachedLeft)) && tabsOverflowContainer, endGradientVisible: ((isRTL && !reachedLeft) || (!isRTL && !reachedRight)) && tabsOverflowContainer, isRTL: isRTL }, children)));
});
ScrollViewEnhanced.displayName = 'ScrollViewEnhanced';
export default ScrollViewEnhanced;
const Container = styled.div(({ scrollOverflow, startGradientVisible, endGradientVisible, isRTL }) => css(css `
      z-index: 2;
      position: relative;
      min-width: 0;
    `, scrollOverflow &&
    css `
        overflow-x: scroll;
        overflow-x: -moz-scrollbars-none;
        -webkit-overflow-scrolling: auto;
        -ms-overflow-style: none;
        scrollbar-width: none;

        mask-image: linear-gradient(
          90deg,
          transparent 0%,
          transparent 1.5%,
          white 10%,
          white 90%,
          transparent 98.5%,
          transparent 100%
        );
        mask-position: center;
        mask-size: 100%;
        transition: ${createCSSTransition('all', MotionEasings.easeOut, MotionDurations.xxSlow)};
        /* needs prefix for transition to work in Chrome/Safari */
        transition-property:
          mask-size,
          -webkit-mask-size,
          mask-position,
          -webkit-mask-position;

        &::-webkit-scrollbar {
          display: none;
          width: 0 !important;
          height: 0 !important;
        }
      `, scrollOverflow &&
    startGradientVisible &&
    !endGradientVisible &&
    css `
        mask-size: 111%;
        mask-position: ${isRTL ? '100%' : '0%'};
      `, scrollOverflow &&
    endGradientVisible &&
    !startGradientVisible &&
    css `
        mask-size: 111%;
        mask-position: ${isRTL ? '0%' : '100%'};
      `, 
// hide both mask gradients when it fits
scrollOverflow &&
    !endGradientVisible &&
    !startGradientVisible &&
    css `
        mask-size: 125%;
      `));
